import React, {useMemo, useEffect } from 'react';
import { Grid, Typography, Box, useTheme, useMediaQuery } from "@material-ui/core"
import clsx from 'clsx';
import { navigate } from "gatsby";
import { submitForm } from "~/services/formSubmit";
import { scrollTo } from "~/utils/helpers";

import {
    Hero,
    Container,
    ContentImage,
    Slider,
    BeforeAfter,
    BlogSlide,
} from '~/components/ui';
import { VideoPlayer } from '~/components/media';
import styles from './styles';
import JoinUsForm from './joinUsForm';
import Advantages from './advantages';

const spacing2x = `${process.env.GATSBY_IMAGES}home/treatCases/spacing@2x.png`;
const crowding2x = `${process.env.GATSBY_IMAGES}home/treatCases/crowding@2x.png`;
const mixed2x = `${process.env.GATSBY_IMAGES}home/treatCases/mixed@2x.png`;
const overbite2x = `${process.env.GATSBY_IMAGES}home/treatCases/overbite@2x.png`;
const underbite2x = `${process.env.GATSBY_IMAGES}home/treatCases/underbite@2x.png`;
const crossbite2x = `${process.env.GATSBY_IMAGES}home/treatCases/crossbite@2x.png`;
const howToStart = `${process.env.GATSBY_IMAGES}linksImage/link1.png`;
const tips = `${process.env.GATSBY_IMAGES}linksImage/link2.png`;
const clinicalPhoto = `${process.env.GATSBY_IMAGES}linksImage/link5.png`;
const bestPractice = `${process.env.GATSBY_IMAGES}linksImage/link6.png`;
const orderContinuation = `${process.env.GATSBY_IMAGES}linksImage/link7.png`;
const brokenAligners = `${process.env.GATSBY_IMAGES}linksImage/link8.png`;
const crowdingBefore = `${process.env.GATSBY_IMAGES}home/beforeAfterSlider/crowding-before.jpg`;
const crowdingAfter = `${process.env.GATSBY_IMAGES}home/beforeAfterSlider/crowding-after.jpg`;
const spacingBefore = `${process.env.GATSBY_IMAGES}home/beforeAfterSlider/spacing-before.jpg`;
const spacingAfter = `${process.env.GATSBY_IMAGES}home/beforeAfterSlider/spacing-after.jpg`;
const videoPreview = `${process.env.GATSBY_IMAGES}video-preview.png`;
const toothImage = `${process.env.GATSBY_IMAGES}home/bestQualities/tooth-image.png`;
const precisionGraphic2x = `${process.env.GATSBY_IMAGES}home/bestQualities/precision-graphic@2x.jpg`;
const doctorPartnership2x = `${process.env.GATSBY_IMAGES}home/bestQualities/doctor-partnership@2x.jpg`;
const toothImageWebp = `${process.env.GATSBY_IMAGES}home/bestQualities/tooth-image.webp`;
const precisionGraphic2xWebP = `${process.env.GATSBY_IMAGES}home/bestQualities/precision-graphic@2x.webp`;
const doctorPartnership2xWebP = `${process.env.GATSBY_IMAGES}home/bestQualities/doctor-partnership@2x.webp`
const yan = `${process.env.GATSBY_IMAGES}home/ourTeam/yan.png`;
const gary = `${process.env.GATSBY_IMAGES}home/ourTeam/gary.png`;
const aaron = `${process.env.GATSBY_IMAGES}home/ourTeam/aaron.png`;

interface IHomeProps {
    location: any
}

export const Home = ({location}: IHomeProps) => {
    const classes = styles();
    const theme = useTheme();
    const isDisplay = useMediaQuery(theme.breakpoints.up('md'));
    const isLaptop = useMediaQuery(theme.breakpoints.up('sm'));

    const slide1 = `${process.env.GATSBY_IMAGES}home/slide1${isDisplay ? 'XL' : (isLaptop ? 'MD' : 'XS')}.png`;
    const slide2 = `${process.env.GATSBY_IMAGES}home/slide2${isDisplay ? 'XL' : (isLaptop ? 'MD' : 'XS')}.png`;
    const slide3 = `${process.env.GATSBY_IMAGES}home/slide3${isDisplay ? 'XL' : (isLaptop ? 'MD' : 'XS')}.png`;
    const slide4 = `${process.env.GATSBY_IMAGES}home/slide4${isDisplay ? 'XL' : (isLaptop ? 'MD' : 'XS')}.png`;
    useEffect(() => {
        let hash = location.hash.replace('#', '');
        scrollTo(hash, 150);
    });

    const cases = [
        { title: 'Spacing', image: spacing2x },
        { title: 'Crowding', image: crowding2x },
        { title: 'Overbite', image: overbite2x },
        { title: 'Underbite', image: underbite2x },
        { title: 'Crossbite', image: crossbite2x },
        { title: 'Mixed', image: mixed2x },
    ]

    const renderedCases = useMemo(() => {
        return cases.map((value) => {
            const { title, image } = value
            return (
                <Grid
                    item
                    key={title}
                    lg={2}
                    md={2}
                    sm={6}
                    xs={6}
                    className={classes.caseItem}
                >
                    <Box className={classes.caseCard}>
                        <Box
                            className={classes.caseIcon}
                            style={{ backgroundImage: `url(${image})` }}
                        />
                        <Typography
                            className={classes.caseTitle}
                            variant="body1"
                        >
                            {title}
                        </Typography>
                    </Box>
                </Grid>
            )
        })
    }, [cases])

    const resources = [
        { icon: howToStart, title: 'How Do I Start a New Case?', url: '//help.orthosnap.com/en/articles/1817055-how-do-i-start-a-new-case' },
        { icon: tips, title: 'Tips on Taking Impressions', url: '//help.orthosnap.com/en/articles/3552877-tips-on-taking-impressions' },
        { icon: clinicalPhoto, title: 'Taking Clinical Photographs', url: '//help.orthosnap.com/en/articles/3561124-taking-clinical-photographs' },
        {
            icon: bestPractice,
            title: 'Our Best Practices on Bite Registration',
            url: '//help.orthosnap.com/en/articles/3561113-bite-registration',
        },
        {
            icon: orderContinuation,
            title: 'How to Order a Continuation?',
            url: '//help.orthosnap.com/en/articles/3683242-how-to-order-a-continuation',
        },
        { icon: brokenAligners, title: 'Lost or Broken Aligners', url: '//help.orthosnap.com/en/articles/3561129-lost-or-broken-aligners' },
    ]

    const renderedResources = useMemo(() => {
        return resources.map((resource, index) => {
            return (
                <Grid
                    item
                    key={index}
                    lg={6}
                    md={6}
                    sm={12}
                    xs={12}
                    className={classes.resourceItem}
                >
                    <Box className={classes.resource} onClick={() => window.open(resource.url, '_blank')}>
                        <Box
                            className={classes.resourceImage}
                            style={{ backgroundImage: `url(${resource.icon})` }}
                        />
                        <Typography
                            variant="subtitle1"
                            className={classes.resourceTitle}
                        >
                            {resource.title}
                        </Typography>
                    </Box>
                </Grid>
            )
        })
    }, [resources])

    const dentists = [
        {
            image: yan,
            name: 'Dr. Yan Pogorelsky',
            position: 'Founder, Orthosnap',
            description:
                '“We’re disrupting the clear aligner space. Our treatment is more effective, precise, faster, and less expensive than 3D printed clear aligners. From complex to simple, we treat 98% of cases, offering dental practitioners the best solution for dental malocclusion.”',
        },
        {
            image: gary,
            name: 'Dr. Gary Herskovits',
            position: 'DDS',
            description:
                '“I have been using clear aligners for many years… Since using Orthosnap, I can see a marked difference in the aligner fit and the tooth movement. Compliant patients are exactly on schedule and their teeth move exactly like the plan.”',
        },
        {
            image: aaron,
            name: 'Dr. Aaron Kohan',
            position: 'Orthodontist',
            description:
                '"As an orthodontist, I use traditional braces, but I have recently performed a number of cases using Orthosnap. The principles behind the design of the Orthosnap process and the full body movement it can achieve are very impressive and it fits very well into the solutions I offer in my practice."',
        },
    ]

    const renderedDentists = useMemo(() => {
        return dentists.map((dentist, index) => {
            return (
                <Grid
                    item
                    lg={4}
                    md={4}
                    sm={12}
                    xs={12}
                    key={index}
                    className={classes.dentistItem}
                >
                    <Box className={classes.dentist}>
                        <Box className={classes.dentistHeader}>
                            <Box
                                className={classes.dentistImage}
                                style={{
                                    backgroundImage: `url(${dentist.image})`,
                                }}
                            />
                            <Box className={classes.dentistName}>
                                <Typography
                                    variant="subtitle2"
                                    className={classes.dentistFullname}
                                >
                                    {dentist.name}
                                </Typography>
                                <Typography
                                    variant="body2"
                                    className={classes.gray1Text}
                                >
                                    {dentist.position}
                                </Typography>
                            </Box>
                        </Box>
                        <Box className={classes.dentistBody}>
                            {dentist.description}
                        </Box>
                    </Box>
                </Grid>
            )
        })
    }, [dentists]);
    const beforeAfterSlider = (<Slider
      className={classes.slider}
      items={[
          <BeforeAfter
            beforeImage={crowdingBefore}
            afterImage={crowdingAfter}
            description="Crowding - 20 steps"
          />,
          <BeforeAfter
            beforeImage={spacingBefore}
            afterImage={spacingAfter}
            description="Spacing - 32 steps"
          />,
          <BeforeAfter
            beforeImage={crowdingBefore}
            afterImage={crowdingAfter}
            description="Crowding - 20 steps"
          />,
          <BeforeAfter
            beforeImage={spacingBefore}
            afterImage={spacingAfter}
            description="Spacing - 32 steps"
          />,
          <BeforeAfter
            beforeImage={crowdingBefore}
            afterImage={crowdingAfter}
            description="Crowding - 20 steps"
          />,
          <BeforeAfter
            beforeImage={spacingBefore}
            afterImage={spacingAfter}
            description="Spacing - 32 steps"
          />,
      ]}
    />);

    return (
        <>
            <Grid container>
                <Grid item lg={9} md={8} sm={12}>
                    <Hero className={classes.hero}>
                        <Typography variant="h2" component="h1" className={classes.heroTitle}>
                            Smiles, Perfected Daily.
                        </Typography>
                        <Typography
                            variant="subtitle1"
                            className={classes.heroSubtitle}
                        >
                            Join leading doctors across the country with the
                            best and clearest aligners in the industry.
                        </Typography>
                    </Hero>
                </Grid>
                <Grid item lg={3} md={4} className={classes.joinUsGridItem}>
                    <Box className={classes.joinUs} id="joinUs">
                        <JoinUsForm onSubmit={submitForm}/>
                    </Box>
                </Grid>
            </Grid>
            <Box className={classes.advantagesBlock}>
                <Container className={classes.advantages}>
                    <Advantages />
                </Container>
            </Box>
            <Box className={classes.mobileJoinUs}>
                <JoinUsForm />
            </Box>
            <Container
                className={clsx(classes.section, classes.afterMobileJoinUs)}
            >
                <Typography
                    variant="overline"
                    className={clsx(classes.sectionTitle, classes.centered)}
                >
                    THE ORTHOSNAP DIFFERENCE
                </Typography>
                <Typography
                    variant="h3"
                    component="h2"
                    className={clsx(classes.h3Title, classes.centered)}
                >
                    Uncommon Precision.
                    <br /> Perfect Alignment.
                </Typography>
                <Typography
                    variant="body1"
                    className={clsx(classes.text, classes.centered)}
                >
                    The precise fit and superior technology of Orthosnap
                    aligners create <br /> better outcomes and beautiful smiles
                    that last. See for yourself!
                </Typography>
                <VideoPlayer
                    className={classes.video}
                    preview={videoPreview}
                    url={process.env.GATSBY_HOME_VIDEO_URL as string}
                />
            </Container>
            <Container id="precision-accuracy" className={classes.section}>
                <ContentImage
                    onButtonClick={() => navigate('/expertise')}
                    imageSrc={toothImage}
                    imageSrcWebP={toothImageWebp}
                    alt="toothImage"
                    buttonText="Learn more"
                    preTitle="PRECISION & ACCURACY"
                    title="Premium quality for faster results"
                    content={
                        <Typography className={classes.text} variant="body1">
                            Increased accuracy gets your patients to complete
                            their treatment and reach a smile-filled future
                            faster. Orthosnap’s superior methods include:
                            resolving cases without any invasive procedures, and
                            a more comfortable fit for your patients by taking
                            their root bone composition into account.
                        </Typography>
                    }
                />
            </Container>
            <Container id="property-cast-mold" className={classes.section}>
                <ContentImage
                    onButtonClick={() => navigate('/expertise')}
                    imageSrc={precisionGraphic2x}
                    imageSrcWebP={precisionGraphic2xWebP}
                    revert
                    alt="precisionGraphic2x"
                    preTitle="PROPRIETARY CAST MOLD"
                    buttonText="Learn more"
                    title="Make a great impression"
                    content={
                        <>
                            <Typography
                                className={classes.text}
                                variant="body1"
                            >
                                The 3D printing process itself introduces
                                inaccuracies due to 3D printers printing from
                                the bottom up. This leads to compounded
                                inaccuracies that undermine the performance of
                                the aligners and creates striation lines, making
                                3D printed aligners less clear.
                            </Typography>
                            <Typography
                                variant="subtitle2"
                                className={classes.contentSubtitle}
                            >
                                Less is More
                            </Typography>
                            <Typography
                                className={classes.text}
                                variant="body1"
                            >
                                Orthosnap is able to pinpoint the perfect places
                                to boost tooth movement without crowding the
                                mouth and produce a superior, more
                                cost-effective product.
                            </Typography>
                        </>
                    }
                />
            </Container>
            <Container id="doctor-partnerships" className={classes.section}>
                <ContentImage
                    onButtonClick={() => navigate('/partnership')}
                    imageSrc={doctorPartnership2x}
                    alt="doctorPartnership2x"
                    imageSrcWebP={doctorPartnership2xWebP}
                    preTitle="DOCTOR PARTNERSHIPS"
                    buttonText="Learn more"
                    title="More smiles. All around."
                    content={
                        <Typography className={classes.text} variant="body1">
                            Orthosnap makes integrating treatment as easy as
                            possible into the services you already offer. We’re
                            here to help every step of the way by providing an
                            online portal for easy access to cases, 24/7 live
                            chat support, digital marketing to drive more
                            patients to your practice, custom product packaging
                            tailored to your practice, and lots more!
                        </Typography>
                    }
                />
            </Container>
            <Box className={classes.grayWrapper}>
                <Container className={classes.section}>
                    <Typography
                        variant="subtitle2"
                        className={classes.subtitle}
                    >
                        Treats all clinical cases
                    </Typography>
                    <Grid container className={classes.casesGrid} spacing={4}>
                        {renderedCases}
                    </Grid>
                </Container>
            </Box>
            {/*<Container className={classes.section}>*/}
            {/*    <Typography variant="h4" component="h3" className={classes.centeredBlueTitle}>*/}
            {/*        Before & After*/}
            {/*    </Typography>*/}
            {/*    <Typography*/}
            {/*        variant="body1"*/}
            {/*        className={classes.centeredDescription}*/}
            {/*    >*/}
            {/*        See the real results of Orthosnap for yourself.*/}
            {/*    </Typography>*/}
            {/*    {beforeAfterSlider}*/}
            {/*</Container>*/}
            <Box className={classes.grayWrapper}>
                <Container id="resource-tips" className={classes.section}>
                    <Typography
                        variant="subtitle2"
                        className={classes.gray1Text}
                    >
                        Resources & Tips
                    </Typography>
                    <Typography variant="body1" className={classes.gray1Text}>
                        Curious to learn more? Check out our extensive resource
                        center.
                    </Typography>
                    <Grid container spacing={4} className={classes.resources}>
                        {renderedResources}
                    </Grid>
                </Container>
            </Box>
            <Box className={classes.section}>
                <Typography variant="h4" component="h3" className={classes.centeredBlueTitle}>
                    From the blog
                </Typography>
                <Slider
                    className={classes.slider}
                    centered
                    firstItemIndex={1}
                    items={[
                        <BlogSlide
                            image={slide1}
                            title="How Orthosnap Differs"
                            onButtonClick={() => window.open('https://blog.orthosnap.com/2018/09/28/how-orthosnap-differs-from-other-aligners/')}
                        />,
                        <BlogSlide
                            image={slide2}
                            title="Encouraging Patient Compliance"
                            onButtonClick={() => window.open('https://blog.orthosnap.com/2019/03/05/encouraging-patient-compliance-during-treatment/')}
                        />,
                        <BlogSlide
                            image={slide3}
                            title="Explaining the Benefits of Orthosnap to Patients"
                            onButtonClick={() => window.open('https://blog.orthosnap.com/2019/06/01/tips-for-explaining-the-benefits-of-orthosnap-to-patients/')}
                        />,
                        <BlogSlide
                          image={slide4}
                          title="4 Things Aligner Patients May Forget"
                          onButtonClick={() => window.open('https://blog.orthosnap.com/2018/09/06/4-things-aligner-patients-may-forget/')}
                        />
                    ]}
                />
            </Box>
            <Box className={classes.grayWrapper}>
                <Container className={classes.section}>
                    <Typography
                        variant="subtitle2"
                        className={classes.gray1Text}
                    >
                        Elite dentists agree
                    </Typography>
                    <Grid container spacing={4} className={classes.dentists}>
                        {renderedDentists}
                    </Grid>
                </Container>
            </Box>
        </>
    )
}
